// Page: SignUp
// Purpose: Email/password and Google signup flows, persists auth, stores
// selected pricing plan for post-signup, and routes to pricing/verify.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { auth, googleAuthProvider } from '../../../firebase/firebase';
import { signInWithPopup, setPersistence, browserLocalPersistence } from 'firebase/auth';
import SocialSignUp from '../../../components/auth/signup/SocialSignUp';
import SignUpForm from '../../../components/auth/signup/SignUpForm';
import { useAuth } from '../../../context/auth';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthLoadingScreen from '../../../components/shared/AuthLoadingScreen';
import { EnterpriseDomainModal } from '../../../components/ui/modals/enterprise-domain-modal.jsx';
import { Building2 } from 'lucide-react';
import api from '../../../api';
import SEOHead from '../../../components/shared/SEOHead';
import Footer from '../../../components/shared/Footer';

export default function SignUp() {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showEnterpriseDomainModal, setShowEnterpriseDomainModal] = useState(false);
  const [enterpriseDomainLoading, setEnterpriseDomainLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, signUpWithGoogle } = useAuth();
  const isDark = useDarkMode();
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Clear errors when component unmounts
    return () => {
      setError(null);
    };
  }, []);

  // Check for errors passed in navigation state
  useEffect(() => {
    if (location.state?.error) {
      setAuthError(location.state.error);
      // Clear the error from the location state to prevent it from showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Handle selected plan from pricing page
  useEffect(() => {
    const selectedPlan = location.state?.selectedPlan;
    if (selectedPlan) {
      console.log('SignUp: Received selected plan from pricing page:', selectedPlan);
      // Store the selected plan for after signup completion
      localStorage.setItem('selectedPlan', JSON.stringify(selectedPlan));
    }
  }, [location.state]);

  const toggleTheme = () => {
    // Use the same pattern as LandingHeader - check the hook value directly
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  // Clear error when user starts typing
  const handleInputChange = () => {
    if (error) {
      setError(null);
    }
    if (authError) {
      setAuthError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    const confirmPassword = formData.get('confirm-password');
    const firstName = formData.get('first-name');
    const lastName = formData.get('last-name');
    const phoneE164 = formData.get('phone_e164') || '';
    const phoneCountry = formData.get('phone_country') || '';
    const phoneNational = formData.get('phone_national') || '';
    const role = (formData.get('role') || '').trim();

    // Input validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!firstName || !lastName) {
      setError('First name and last name are required');
      return;
    }

    setIsLoading(true);

    try {
      // Call signUp - it should handle Firebase auth creation AND server profile creation (which defaults plan)
      console.log('Calling signUp with extra profile fields: phone and role');
      const extraProfile = {
        phone: {
          e164: phoneE164,
          national: phoneNational,
          country: phoneCountry
        },
        role,
        hasSeenContactInfoModal: true
      };
      // IMPORTANT: Pass (plan, rememberMe, extraProfile) only — extra args are ignored.
      const user = await signUp(email, password, firstName, lastName, 'basic', false, extraProfile);

      // If we get here, both Firebase and server-side signup were successful
      console.log('Regular signup successful - navigating to pricing page');
      
      // Set a flag to indicate successful signup - the pricing page can check for this
      localStorage.setItem('just_signed_up', 'true');
      // Set the justLoggedIn flag to indicate successful registration
      sessionStorage.setItem('justLoggedIn', 'true');
      // Set the coming_from_signup flag for better handling in the pricing page
      sessionStorage.setItem('coming_from_signup', 'true');
      // Set auth refresh flag for the pricing page to properly handle auth state 
      // BUT we'll handle it without a full page refresh
      sessionStorage.setItem('needs_auth_refresh', 'true');
      
      // CRITICAL: Force Firebase persistence to LOCAL for maximum reliability
      try {
        const { setPersistence, browserLocalPersistence } = await import('firebase/auth');
        const authInstance = await import('../../../firebase/firebase').then(m => m.auth);
        
        // Force persistence to LOCAL to survive redirects
        await setPersistence(authInstance, browserLocalPersistence);
        console.log('Firebase auth persistence set to LOCAL');
        
        // Force token refresh to ensure we have valid claims
        if (authInstance.currentUser) {
          await authInstance.currentUser.getIdToken(true);
          console.log('Firebase ID token refreshed');
          
          // Store auth info for recovery after Stripe redirect
          const authBackup = {
            uid: authInstance.currentUser.uid,
            email: authInstance.currentUser.email,
            timestamp: Date.now(),
            isFromSignup: true
          };
          localStorage.setItem('auth_backup', JSON.stringify(authBackup));
          console.log('Created auth backup for potential recovery');
          
          // Create a timestamp to track recency for auth recovery
          localStorage.setItem('auth_timestamp', Date.now().toString());
        }
      } catch (persistenceError) {
        console.error('Error setting persistence:', persistenceError);
        // Continue anyway - this is just a precaution
      }
      
      // Show AuthLoadingScreen during transition
      setIsRedirecting(true);
      
      // Add a short delay before navigating to ensure the profile is fully created on the server
      // and Firebase auth state is fully initialized
      console.log('Adding a short delay to ensure profile is fully created and auth is stable...');
      setTimeout(() => {
        // Use React Router for a smoother transition
        navigate('/pricing');
      }, 2000); // Increased to 2 seconds to ensure auth stability
      
      // Keep isLoading true until navigation completes
      
    } catch (err) {
      // Log detailed error information for debugging
      console.error('Signup error:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        name: err.name,
        stack: err.stack,
        error: err.error, // Check if error object contains more useful info
        stringified: JSON.stringify(err)
      });

      // Determine appropriate error message
      let errorMessage = 'Failed to create an account. Please try again.'; // Default message

      // Check specific Firebase error codes
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please try logging in instead.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      // Check for custom error messages potentially passed from AuthContext or backend
      } else if (err.errorCode === 'EMAIL_ALREADY_EXISTS' || 
                 (err.message && err.message.includes('An account with this email address already exists')) ||
                 err.message?.includes('EMAIL_EXISTS') || 
                 err.error?.message === 'EMAIL_EXISTS') {
        // Example check if backend sends specific string or object
        errorMessage = 'This email is already registered. Please try logging in instead.';
      } else if (err.message && typeof err.message === 'string') {
        // Use the error message if it's provided and seems user-friendly
        // Avoid showing generic "Network Error" or overly technical messages directly if possible
        if (!err.message.toLowerCase().includes('network error')) {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // CRITICAL FIX: Clear all auth errors at the start to prevent blocking
    setAuthError(null);
    localStorage.removeItem('refrral_auth_error');
    sessionStorage.removeItem('google_signin_error');
    console.log('Cleared all auth errors before Google sign-up attempt');
    
    // IMPORTANT: Set a flag to prevent AuthContext from interfering during signup
    sessionStorage.setItem('googleSignUpInProgress', 'true');
    
    // Clear any leftover flags from failed sign-ups
    sessionStorage.removeItem('justLoggedIn');
    localStorage.removeItem('just_signed_up');
    
    try {
      // Check if auth is initialized
      if (!auth) {
        throw new Error('Authentication service unavailable. Please try again later.');
      }
      
      // OPTIMIZED: Ensure Firebase persistence is set to LOCAL before sign-up attempt
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('Firebase persistence set to LOCAL before Google sign-up');
      } catch (persistenceError) {
        console.warn('Failed to set persistence before Google sign-up:', persistenceError);
        // Continue with sign-up anyway
      }
      
      // NOW set loading state right before showing popup
      setGoogleLoading(true);
      console.log('Google sign-up: Setting loading state and showing popup');
      
      // Get user info with Google sign-up
      let result, firebaseUser;
      
      try {
        // Try popup first
        result = await signInWithPopup(auth, googleAuthProvider);
        firebaseUser = result.user;
      } catch (popupError) {
        // Clear loading on popup error
        setGoogleLoading(false);
        // Clear the progress flag on error
        sessionStorage.removeItem('googleSignUpInProgress');
        
        // Check if popup was blocked
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.message?.includes('popup')) {
          console.log('Popup was blocked, falling back to redirect method');
          
          // Import signInWithRedirect as fallback
          const { signInWithRedirect } = await import('firebase/auth');
          await signInWithRedirect(auth, googleAuthProvider);
          return; // Exit here, redirect will handle the rest
        }
        
        // If it's not a popup issue, rethrow the error
        throw popupError;
      }
      
      // Popup completed successfully, now clear loading temporarily while processing
      setGoogleLoading(false);
      console.log('Google popup completed, processing sign-up...');
      
      // OPTIMIZED: After successful popup, just verify we have a token, don't force refresh
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('Firebase persistence re-confirmed after Google popup');
        
        // Just verify we can get a token, the auth functions will handle fresh tokens when needed
        const token = await firebaseUser.getIdToken(false);
        console.log('Verified Firebase token after Google sign-up, length:', token?.length);
      } catch (tokenError) {
        console.error('Error verifying token after Google sign-up:', tokenError);
        // Don't fail the sign-up, but warn about potential issues
      }
      
      // Get display name from Google account
      const displayName = firebaseUser.displayName || '';
      let firstName = '';
      let lastName = '';
      
      // Parse the display name if it exists
      if (displayName) {
        const nameParts = displayName.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }
      
      console.log('Calling signUpWithGoogle to create server-side profile...');
      
      // Use the signUpWithGoogle function from auth context
      const user = await signUpWithGoogle(firebaseUser, firstName, lastName);
      
      // Clear the progress flag after successful signup
      sessionStorage.removeItem('googleSignUpInProgress');
      
      // Successfully signed up with Google
      console.log('Google signup successful - navigating to pricing page');
      console.log('Sign-up successful via Google:', {
        user: user ? {
          uid: user.uid,
          planLevel: user.planLevel,
          email: user.email,
          subscriptionStatus: user?.subscriptionStatus
        } : 'No user data'
      });
      
      // Add a short delay to ensure auth state is stable
      console.log('Adding a short delay to ensure auth state is stable...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // CRITICAL: Store additional auth recovery data in localStorage (like regular signUp does)
      try {
        const authRecoveryData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          provider: 'google.com',
          timestamp: Date.now(),
          firstName,
          lastName
        };
        localStorage.setItem('auth_recovery_data', JSON.stringify(authRecoveryData));
        localStorage.setItem('google_signup_timestamp', Date.now().toString());
        sessionStorage.setItem('auth_method', 'google');
        console.log('Google sign-up auth recovery data saved to localStorage');
      } catch (storageError) {
        console.warn('Failed to save Google auth recovery data:', storageError);
      }
      
      // Set just signed up flag for proper navigation
      localStorage.setItem('just_signed_up', 'true');
      sessionStorage.setItem('justLoggedIn', 'true');
      sessionStorage.setItem('coming_from_signup', 'true');
      
      // Navigate to pricing page
      console.log('Navigating to pricing page');
      navigate('/pricing', { 
        replace: true,
        state: { fromSignup: true }
      });
      
    } catch (err) {
      console.error('Google sign-up error:', err);
      
      // Clear the progress flag on error
      sessionStorage.removeItem('googleSignUpInProgress');
      
      // IMPORTANT: Detect when user cancels the popup or other auth errors occur
      // Specific error code for popup close
      if (err.code === 'auth/popup-closed-by-user' || 
          err.code === 'auth/cancelled-popup-request' ||
          err.code === 'auth/popup-blocked') {
        console.log('User cancelled Google sign-up popup');
        setGoogleLoading(false);
        return; // Just stop loading, don't show an error
      }
      
      // Handle Firebase initialization errors
      if (err.code === 'app/no-app' || err.message?.includes('initializeApp()')) {
        setAuthError('Authentication service is initializing. Please try again in a moment.');
        setGoogleLoading(false);
        return;
      }
      
      // Always set the loading to false for any other error
      setGoogleLoading(false);
      
      // Debugging: Log the full error object to understand what's coming through
      console.log('Error details:', {
        message: err.message,
        code: err.code,
        name: err.name,
        status: err.status,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : 'No response data',
        stack: err.stack ? err.stack.slice(0, 200) : 'No stack'
      });
      
      let errorMsg = '';
      // Handle specific error types
      if (err.code === 'auth/account-exists-with-different-credential' || 
          (err.message && err.message.includes('already associated with an email/password account'))) {
        console.log('Setting credential error');
        errorMsg = 'This email already has an account with password. Please sign in with your email and password instead of using Google.';
      } else if (err.code === 'auth/email-already-in-use' || 
                err.errorCode === 'EMAIL_ALREADY_EXISTS' ||
                (err.message && err.message.includes('An account with this email address already exists')) ||
                (err.message && err.message.includes('email is already registered')) ||
                (err.message && err.message.includes('user profile for this account already exists')) ||
                (err.message && err.message.includes('already exists'))) {
        // User tried to sign up with Google but account already exists
        console.log('Setting existing account error');
        const existingAccountErrorMsg = 'An account with this Google email already exists. Please sign in instead by clicking "Sign in here" below.';
        // Set flag to prevent redirects
        sessionStorage.setItem('google_signin_error', 'true');
        sessionStorage.setItem('google_signin_error_timestamp', Date.now().toString());
        
        // Explicitly navigate back to sign-up page with the error message
        console.log('Explicitly navigating back to sign-up page after Google sign-up existing account');
        navigate('/signup', { replace: true, state: { error: existingAccountErrorMsg } });
        return; // Stop further execution
        
      } else if (err.message && err.message.includes('profile')) {
        // Generic profile creation error (but not "already exists" case)
        errorMsg = 'Error creating your profile. Please try again.';
      } else if (err.message) {
        // For any other errors with a message
        console.log('Setting generic error message');
        errorMsg = `Failed to sign up with Google: ${err.message}`;
      } else {
        // Generic error fallback
        console.log('Setting fallback error message');
        errorMsg = 'Failed to sign up with Google. Please try again.';
      }
      
      console.log('Setting error for Google sign-up:', errorMsg);
      setAuthError(errorMsg);
    }
  };

  const handleEnterpriseSignup = () => {
    setShowEnterpriseDomainModal(true);
  };

  const handleEnterpriseDomainSubmit = async (domain) => {
    setEnterpriseDomainLoading(true);
    try {
      // Check if enterprise exists for this domain
      const response = await api.get(`/enterprise/check-domain/${domain}`);
      
      console.log('Enterprise domain check response:', response);
      
      // Handle different response structures
      const responseData = response.data || response;
      const exists = responseData.exists;
      const enterprise = responseData.enterprise;
      
      if (exists && enterprise) {
        // Enterprise exists, navigate to enterprise signup page
        navigate('/enterprise/signup', { 
          state: { 
            enterprise: enterprise,
            domain 
          } 
        });
      } else {
        // No enterprise found for this domain
        setError('No enterprise plan found for this domain. Please contact your administrator or sign up with a regular account.');
        setShowEnterpriseDomainModal(false);
      }
    } catch (err) {
      console.error('Error checking enterprise domain:', err);
      console.error('Error response:', err.response);
      
      // Check if it's a 404 (no enterprise found)
      if (err.response && err.response.status === 404) {
        setError('No enterprise plan found for this domain. Please contact your administrator or sign up with a regular account.');
      } else {
        setError('Error checking enterprise domain. Please try again.');
      }
      setShowEnterpriseDomainModal(false);
    } finally {
      setEnterpriseDomainLoading(false);
    }
  };

  // If we're in the redirecting state, show the AuthLoadingScreen
  if (isRedirecting) {
    return <AuthLoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <SEOHead pageType="signup" />
      
      {/* Landing header with toggle theme button */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/"
        showLogo={true}
      />
      
      {/* Content section */}
      <div className="bg-white dark:bg-gray-900 px-4 py-12 w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
          <div className="text-center">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">Join Advocacy Lab</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight max-w-[90%] md:max-w-full mx-auto">
              Create your account
            </p>
            <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 max-w-[90%] md:max-w-full mx-auto">
              Start creating high-impact content for your campaigns.
            </p>
          </div>
        </div>
        
        {/* Simple in-page error message */}
        {(error || authError) && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px] rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-200">{error || authError}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Email/Password Sign Up Form */}
        <SignUpForm 
          onSubmit={handleSubmit} 
          isLoading={isLoading}
          onInputChange={handleInputChange}
        />
        
        {/* Google Sign Up */}
        <SocialSignUp 
          onGoogleSignUp={handleGoogleSignUp} 
          isLoading={googleLoading}
          onInputChange={handleInputChange}
          onEnterpriseSignUp={handleEnterpriseSignup}
        />
      </div>
      
      {/* Enterprise Domain Modal */}
      <EnterpriseDomainModal
        isOpen={showEnterpriseDomainModal}
        onClose={() => setShowEnterpriseDomainModal(false)}
        onContinue={handleEnterpriseDomainSubmit}
        isLoading={enterpriseDomainLoading}
        actionType="signup"
      />
      
      <Footer />
    </div>
  );
}